import { useState } from 'react';

/**
 * The narrowing controls on a surface, folded away until they are wanted.
 *
 * They are the noisiest thing on a page you mostly READ, and on both surfaces
 * they were permanently open for the sake of the few moments they are used.
 * So they collapse to one control — and the state is remembered per surface,
 * because someone who filters on every visit should not re-open it every time.
 *
 * A mode switch is NOT one of these and never folds in here. Which rows are on
 * screen is a question; how they are drawn is not, and a layout you cannot
 * reach without opening a filter panel is a layout nobody finds.
 */

export function useFilterRow(key: string, active: boolean): {
  open: boolean;
  toggle: () => void;
} {
  const storageKey = `filters.open.${key}`;
  const [remembered, setRemembered] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    const next = !remembered;
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* storage unavailable; the fold still works, just not across visits */
    }
    setRemembered(next);
  };

  // A filter in effect always shows its controls. Hiding one is how a surface
  // ends up quietly holding back rows with nothing on screen saying why.
  return { open: remembered || active, toggle };
}
